import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { TenantResolverService } from '@app/shared/tenant-resolver.service';
import { ColorUtils } from '@app/shared/utilities/color.utils';
import { ThemeProperty } from '@app/tenants/models/theme-property';
import { palette, updatePrimaryPalette } from '@primeuix/themes';
import { PaletteDesignToken } from '@primeuix/themes/types';

@Injectable({ providedIn: 'root' })
export class ThemeService {

	private readonly defaultPrimaryColor = '#1e9de2';
	
	constructor (
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly tenantResolverService: TenantResolverService
	) { }

	initialize (): void {
		this.applyTheme();
	}

	setProperty (property: string, value: string): void {
		this.document.documentElement.style.setProperty(property, value);
	}

	getProperty (property: string, location: HTMLElement = this.document.documentElement): string {
		return getComputedStyle(location).getPropertyValue(property);
	}

	mapColorToProperty (prop: string, value: string): void {
		switch (prop) {
			case ThemeProperty.Primary:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-primary', value);
				this.applyPrimeNgTheme(value);
				break;
			case ThemeProperty.Secondary:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-secondary', value);
				break;
			case ThemeProperty.Accent:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-accent', value);
				break;
			case ThemeProperty.TopBar:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-topbar-base', value);
				this.setCssCustomPropToForegroundColorFromValue('--color-topbar-text', value, '255,255,255', '0,0,0');
				this.setCssCustomPropToForegroundColorFromValue('--color-main-search-bg', value, '255,255,255', '0,0,0', 0.179);
				break;
			case ThemeProperty.SideNav:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-side-navigation-base', value);
				this.setCssCustomPropToForegroundColorFromValue('--color-side-navigation-text', value, '255,255,255', '0,0,0');
				break;
			case ThemeProperty.SideNavScroll:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-side-navigation-scrollbar', value);
				break;
			case ThemeProperty.Base:
				this.setProperty('--color-base', value ? value : null);
				break;
			case ThemeProperty.Loaders:
				this.setCssCustomPropToRgbValuesFromColorObj('--color-loaders', value);
				break;
			case ThemeProperty.CardHeader:
				this.setProperty('--surface-main-header', value ? value : null);
				break;
			case ThemeProperty.CardFooter:
				this.setProperty('--surface-main-footer', value ? value : null);
				break;
		}
	}

	applyTheme (): void {
		const tenant = this.tenantResolverService.getTenant();
		const theme = tenant.theme;
		const loginImage = tenant.loginImage;
		if (theme) {
			theme.primary ??= this.defaultPrimaryColor;
			Object.keys(theme).forEach(prop => this.mapColorToProperty(prop, theme[prop]));
		}
		if (loginImage) {
			this.setProperty('--login-image', `url(${ loginImage })`);
		}
	}

	private setCssCustomPropToRgbValuesFromColorObj (cssCustomProp: string, value: string): void {
		const { r = null, g = null, b = null } = value ? ColorUtils.hexToRGB(value) : {};
		this.setProperty(cssCustomProp, value ? `${ r }, ${ g }, ${ b }` : null);
	}

	private setCssCustomPropToForegroundColorFromValue (cssCustomProp: string, value: string, lightRgb: string, darkRgb: string, threshold?: number): void {
		this.setProperty(cssCustomProp, value ? ColorUtils.pickForegroundColor(value, lightRgb, darkRgb, threshold) : null);
	}

	private applyPrimeNgTheme (hexColor: string = this.defaultPrimaryColor): void {
		const colorShades = palette(hexColor) as PaletteDesignToken;
		updatePrimaryPalette(colorShades);
	}
}